/* Notification Center — Email Template picker + preview.
 *
 * Used by BOTH the Rule Detail → Email Template tab and the New Rule wizard →
 * Email Template step, so the two can never drift. The options are the LIVE
 * Email Templates (the store's `templates`, hydrated from
 * dbo.AI_NotificationTemplate — never a hardcoded list), and the rule stores the
 * chosen template's ID (AI_NotificationRule.EmailTemplateId): a REFERENCE, never a
 * copy, so renaming or editing the template shows up on every rule that uses it.
 *
 * The preview shows the template's subject + body with the {{tokens}} left as
 * placeholders — they resolve only at send time. A rich-text (HTML) body is shown
 * as plain text, so stored markup is never injected into the page.
 *
 * CONTROLLED: the parent owns `value` (template id, or null = no template). */
import { Fragment, useId } from 'react';
import { useNcStore } from './store';
import type { NcTemplate } from './data';

/** A template body as display text: plain text as-is; an HTML (rich-text) body
 *  converted to its text with line breaks kept. Parsed with DOMParser (inert — no
 *  script runs, nothing is inserted into the page). */
export function templateBodyText(body?: string | null): string {
  const raw = body ?? '';
  if (!/<[a-z][\s\S]*>/i.test(raw)) return raw;
  const doc = new DOMParser().parseFromString(raw, 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6').forEach((el) => el.append('\n'));
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

const optionLabel = (t: NcTemplate) => `${t.name}${t.category ? ` (${t.category})` : ''}${t.active ? '' : ' — inactive'}`;

export function EmailTemplatePicker({ value, onChange, readOnly = false }: {
  /** Selected template id; null = no template. */
  value: string | null;
  onChange: (id: string | null) => void;
  readOnly?: boolean;
}) {
  const selectId = useId();
  const templates = useNcStore((s) => s.templates);   // live, DB-backed — no mock data
  // Only templates with a persisted id can be linked (a template created moments
  // ago gets its id as soon as its save returns).
  const linkable = templates.filter((t): t is NcTemplate & { id: string } => !!t.id);
  const tpl = value ? linkable.find((t) => t.id === value) : undefined;
  // Offer active templates, plus the current one even if it was since deactivated
  // (so the select never silently shows a different value than what is saved).
  const options = linkable.filter((t) => t.active || t.id === value);
  const missing = !!value && !tpl;

  return (
    <>
      <div className="nc-form-group">
        <label className="nc-flabel" htmlFor={selectId}>Template</label>
        <select id={selectId} className="nc-fselect" value={value ?? ''} disabled={readOnly}
          onChange={(e) => onChange(e.target.value || null)}>
          <option value="">— No template (built-in email) —</option>
          {missing && <option value={value!}>(Template no longer exists)</option>}
          {options.map((t) => <option key={t.id} value={t.id}>{optionLabel(t)}</option>)}
        </select>
      </div>
      <div className="nc-form-group">
        <label className="nc-flabel">Preview</label>
        <div className="nc-tpl-preview">
          {tpl ? (
            <>
              <div className="nc-tpl-preview-subj">{tpl.subject}</div>
              {templateBodyText(tpl.body).split('\n').map((line, i) => <Fragment key={i}>{line}<br /></Fragment>)}
            </>
          ) : (
            <span className="nc-tpl-preview-none">
              {missing
                ? 'The linked template has been deleted — the built-in permit-limit email is sent until another template is chosen.'
                : 'No template selected — the built-in permit-limit email is sent.'}
            </span>
          )}
        </div>
        {tpl && !tpl.active && (
          <div className="nc-form-hint nc-tpl-warn">This template is inactive — the built-in email is sent until it is re-activated.</div>
        )}
        <div className="nc-form-hint">Tokens such as {'{{SampleId}}'} and {'{{Result}}'} resolve at send time. Edit the template on the Email Templates screen.</div>
      </div>
    </>
  );
}
